import { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import type { LeadStatus } from "../types";
import { VALID_TRANSITIONS, STATUS_COLOR } from "../constants";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { useLocale } from "../../../context/LanguageContext";

interface UpdateStatusDialogProps {
  open: boolean;
  currentStatus: LeadStatus;
  leadName: string;
  onClose: () => void;
  onConfirm: (newStatus: LeadStatus, note?: string) => void;
  loading: boolean;
}

export function UpdateStatusDialog({
  open,
  currentStatus,
  leadName,
  onClose,
  onConfirm,
  loading,
}: UpdateStatusDialogProps) {
  const { t } = useLocale();
  const [selected, setSelected] = useState<LeadStatus | "">("");
  const [note, setNote] = useState("");

  if (!open) return null;

  const validNext = VALID_TRANSITIONS[currentStatus] ?? [];
  const canSubmit = selected !== "" && !loading;

  const handleConfirm = () => {
    if (!canSubmit || !selected) return;
    onConfirm(selected as LeadStatus, note.trim() || undefined);
    setSelected("");
    setNote("");
  };

  const handleClose = () => {
    setSelected("");
    setNote("");
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t("leads.statusChange.title")}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-5">
            <p className="text-sm text-muted-foreground">
              {t("leads.statusChange.description").replace("{name}", leadName)}
            </p>

            {/* Current → New */}
            <div className="flex items-center gap-3">
              <LeadStatusBadge status={currentStatus} />
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              {selected ? (
                <LeadStatusBadge status={selected as LeadStatus} />
              ) : (
                <span className="text-sm text-muted-foreground">?</span>
              )}
            </div>

            {/* Status Options */}
            <div className="flex flex-wrap gap-2">
              {validNext.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setSelected(status)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    selected === status
                      ? `${STATUS_COLOR[status]} ring-2 ring-primary`
                      : `${STATUS_COLOR[status]} opacity-70 hover:opacity-100`
                  }`}
                >
                  {t(`leads.status.${status}`)}
                </button>
              ))}
            </div>

            {/* Optional Note */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.statusChange.noteLabel")}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={t("leads.statusChange.notePlaceholder")}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t("common.loading") : t("common.confirm")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
